import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card, useCategoryLabel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Trophy, Target, ListChecks, AlertTriangle, PhoneCall, FileText, ArrowRight } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="flex items-center gap-4">
      <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
        <Icon className="h-6 w-6" style={{ color: accent }} />
      </div>
      <div>
        <div className="text-2xl font-head font-bold text-slate-900">{value}</div>
        <div className="text-sm text-slate-500">{label}{sub && <span className="text-slate-400"> · {sub}</span>}</div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const catLabel = useCategoryLabel();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/analytics/dashboard").then((r) => setData(r.data)); }, []);

  const quick = [
    { icon: ListChecks, key: "mcq", to: "/mcq", accent: "#2563EB" },
    { icon: PhoneCall, key: "liveCall", to: "/live-call", accent: "#059669" },
    { icon: FileText, key: "ncrpForm", to: "/ncrp-form", accent: "#7C3AED" },
  ];

  return (
    <div>
      <PageHeader title={`${t("welcome")}, ${user?.name}`} subtitle={t("appSub")} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Stat icon={Target} label={t("avgScore")} value={`${data?.avg_score ?? 0}%`} accent="#2563EB" />
        <Stat icon={ListChecks} label={t("totalSessions")} value={data?.total_sessions ?? 0} accent="#059669" />
        <Stat icon={Trophy} label={t("rank")} value={`#${data?.rank ?? "-"}`} sub={`/ ${data?.total_trainees ?? 0}`} accent="#D97706" />
        <Stat icon={AlertTriangle} label="Your Mistakes" value={data?.user_mistakes?.length ?? 0} accent="#DC2626" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <h3 className="font-head font-semibold text-slate-900 mb-4">{t("categoryProgress")}</h3>
          {data?.category_progress?.length ? (
            <div className="space-y-4">
              {data.category_progress.map((c) => (
                <div key={c.category_code} data-testid={`progress-${c.category_code}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-700 font-medium">{catLabel(c.category_code)}</span>
                    <span className="text-slate-500">{c.avg}% · {c.attempts} {t("attempts")}</span>
                  </div>
                  <Progress value={c.avg} className="h-2" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-sm">{t("noData")}</p>
          )}
        </Card>

        <div className="space-y-6">
          <Card>
            <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" /> {t("topMistakes")}
            </h3>
            {data?.global_mistakes?.length ? (
              <ul className="space-y-2">
                {data.global_mistakes.map((m, i) => (
                  <li key={i} className="flex justify-between items-start gap-2 text-sm border-b border-slate-100 pb-2 last:border-0">
                    <span className="text-slate-700">{m.mistake}</span>
                    {!!m.count && <span className="shrink-0 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-xs font-medium">{m.count}×</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500 text-sm">No global mistakes to show.</p>
            )}
          </Card>

          <Card>
            <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Target className="h-4 w-4 text-blue-500" /> Your Mistakes
            </h3>
            {data?.user_mistakes?.length ? (
              <ul className="space-y-2">
                {data.user_mistakes.map((m, i) => (
                  <li key={i} className="flex justify-between items-start gap-2 text-sm border-b border-slate-100 pb-2 last:border-0">
                    <span className="text-slate-700">{m.mistake}</span>
                    <span className="shrink-0 rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 text-xs font-medium">{m.count}×</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-emerald-600 text-sm">{t("noMistakes")}</p>
            )}
          </Card>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="font-head font-semibold text-slate-900 mb-4">{t("quickStart")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quick.map((q) => (
            <Card key={q.key} className="hover:shadow-md transition-shadow cursor-pointer group" onClick={() => navigate(q.to)}
              data-testid={`quickstart-${q.key}`}>
              <div className="flex items-center justify-between">
                <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${q.accent}15` }}>
                  <q.icon className="h-5 w-5" style={{ color: q.accent }} />
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-slate-600 transition-colors" />
              </div>
              <div className="mt-4 font-head font-semibold text-slate-900">{t(q.key)}</div>
              <div className="text-sm text-slate-500 mt-1">{t("startTraining")}</div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
